import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "../i18n/I18nProvider";
import {
  PUBLIC_ZIPCODE_BY_ZIP,
  PUBLIC_ZIPCODE_SUGGESTIONS,
} from "../gql/publicZipcode";
import {
  PublicZipcodeResponse,
  PublicZipcodeSuggestionsResponse,
  ZipcodeByZip,
  ZipcodeSuggestion,
} from "../gql/types/IResponseZipCode";
import { getPublicAccessToken } from "../utils/publicToken";
import { QuoteHolderFormState } from "../types/leadWizard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

interface QuoteHolderStepProps {
  initialValues: QuoteHolderFormState;
  selectedHouseholdTypeIcon?: string | null;
  onSubmit: (values: QuoteHolderFormState) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

export const QuoteHolderStep: React.FC<QuoteHolderStepProps> = ({
  initialValues,
  selectedHouseholdTypeIcon,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] =
    useState<QuoteHolderFormState>(initialValues);
  const [error, setError] = useState<string | null>(null);
  const [zipRecord, setZipRecord] = useState<ZipcodeByZip | null>(null);
  const [changingZip, setChangingZip] = useState(false);
  const [zipQuery, setZipQuery] = useState("");
  const [zipSuggestions, setZipSuggestions] = useState<ZipcodeSuggestion[]>([]);
  const [zipError, setZipError] = useState<string | null>(null);

  const publicToken = getPublicAccessToken();
  const [fetchZipcode, { loading: loadingZip }] = useLazyQuery<
    PublicZipcodeResponse,
    { zipCode: string; token: string }
  >(PUBLIC_ZIPCODE_BY_ZIP, { fetchPolicy: "no-cache" });
  const [fetchSuggestions] = useLazyQuery<
    PublicZipcodeSuggestionsResponse,
    { prefix: string; token: string; limit?: number }
  >(PUBLIC_ZIPCODE_SUGGESTIONS, { fetchPolicy: "network-only" });

  // Resolve county name / options for the zip we already have.
  useEffect(() => {
    if (!initialValues.zipCode) return;
    fetchZipcode({
      variables: { token: publicToken, zipCode: initialValues.zipCode },
    })
      .then(({ data }) => setZipRecord(data?.publicZipcodeByZip ?? null))
      .catch((err) => console.warn("[public] zipcode lookup failed", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const countyOptions = useMemo(() => {
    if (!zipRecord) return [] as { fips: string; name: string }[];
    const fips = (zipRecord.countyFipsAll || "")
      .split("|")
      .map((v) => v.trim())
      .filter(Boolean);
    const names = (zipRecord.countyNamesAll || "")
      .split("|")
      .map((v) => v.trim())
      .filter(Boolean);
    if (fips.length === 0) {
      return [
        {
          fips: zipRecord.countyFips ?? "",
          name: zipRecord.countyName ?? zipRecord.countyFips ?? "",
        },
      ];
    }
    return fips.map((f, i) => ({ fips: f, name: names[i] ?? f }));
  }, [zipRecord]);

  const applyZipRecord = (record: ZipcodeByZip) => {
    setZipRecord(record);
    setFormValues((prev) => ({
      ...prev,
      zipCode: record.zipCode,
      stateCode: (record.stateId ?? "").toUpperCase(),
      stateName: record.stateName,
      countyFips: record.countyFips ?? "",
      city: prev.city.trim() ? prev.city : record.city ?? "",
    }));
    setChangingZip(false);
    setZipQuery("");
    setZipSuggestions([]);
    setZipError(null);
  };

  const lookupZip = async (zip: string) => {
    try {
      const { data } = await fetchZipcode({
        variables: { token: publicToken, zipCode: zip },
      });
      const record = data?.publicZipcodeByZip ?? null;
      if (record) {
        applyZipRecord(record);
      } else {
        setZipError(t("quote.holder.errors.zipNotFound"));
      }
    } catch (err) {
      console.warn("[public] zipcode lookup failed", err);
      setZipError(t("quote.holder.errors.zipNotFound"));
    }
  };

  const handleZipQueryChange = async (value: string) => {
    const digits = value.replace(/[^0-9]/g, "").slice(0, 5);
    setZipQuery(digits);
    setZipError(null);
    if (digits.length === 5) {
      setZipSuggestions([]);
      await lookupZip(digits);
    } else if (digits.length >= 3) {
      try {
        const { data } = await fetchSuggestions({
          variables: { token: publicToken, prefix: digits, limit: 5 },
        });
        setZipSuggestions(data?.publicZipcodeSuggestions ?? []);
      } catch (err) {
        console.warn("[public] zipcode suggestions failed", err);
        setZipSuggestions([]);
      }
    } else {
      setZipSuggestions([]);
    }
  };

  const handleChange = (
    field: keyof QuoteHolderFormState,
    value: string | boolean
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isPlaceholderEmail = (value: string) =>
    /^no-email-.*@example\.com$/i.test(value.trim());
  const emailEditable =
    !initialValues.email.trim() || isPlaceholderEmail(initialValues.email);

  const validate = (): boolean => {
    if (
      !formValues.email.trim() ||
      isPlaceholderEmail(formValues.email) ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.email.trim())
    ) {
      setError(t("quote.holder.errors.email"));
      return false;
    }
    if (!formValues.address1.trim()) {
      setError(t("quote.holder.errors.address1"));
      return false;
    }
    if (!formValues.city.trim()) {
      setError(t("quote.holder.errors.city"));
      return false;
    }
    if (!formValues.stateCode.trim()) {
      setError(t("quote.holder.errors.stateCode"));
      return false;
    }
    if (!formValues.zipCode.trim()) {
      setError(t("quote.holder.errors.zipCode"));
      return false;
    }
    if (!formValues.ssn.trim()) {
      setError(t("quote.holder.errors.ssn"));
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formValues);
    } catch (err: any) {
      setError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <button
          type="button"
          className="button button-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("buttons.back")}
        </button>
      </div>
      <div className="card-body">
        <div className="household-type-selection-header">
          <div>
            <h3 className="card-title">{t("quote.holder.title")}</h3>
            <p className="section-helper">{t("quote.holder.helper")}</p>
          </div>
          {selectedHouseholdTypeIcon && (
            <div className="household-type-selected">
              <span aria-hidden="true" className="household-type-icon">
                {selectedHouseholdTypeIcon}
                <FontAwesomeIcon
                  className="fa-check step-3"
                  icon={faCircleCheck}
                />
              </span>
            </div>
          )}
        </div>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderFirstName">
                <Form.Label>{t("quote.holder.firstName")}</Form.Label>
                <Form.Control value={formValues.firstName} disabled readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="holderLastName">
                <Form.Label>{t("quote.holder.lastName")}</Form.Label>
                <Form.Control value={formValues.lastName} disabled readOnly />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderEmail">
                <Form.Label>{t("quote.holder.email")}</Form.Label>
                <Form.Control
                  type="email"
                  value={isPlaceholderEmail(formValues.email) ? "" : formValues.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  disabled={!emailEditable}
                  readOnly={!emailEditable}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="holderPhone">
                <Form.Label>{t("quote.holder.phone")}</Form.Label>
                <Form.Control value={formValues.phone} disabled readOnly />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="holderAddress1" className="mb-3">
            <Form.Label>{t("quote.holder.address1")}</Form.Label>
            <Form.Control
              value={formValues.address1}
              onChange={(event) => handleChange("address1", event.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="holderAddress2" className="mb-3">
            <Form.Label>{t("quote.holder.address2")}</Form.Label>
            <Form.Control
              value={formValues.address2}
              onChange={(event) => handleChange("address2", event.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="holderCity" className="mb-3">
            <Form.Label>{t("quote.holder.city")}</Form.Label>
            <Form.Control
              value={formValues.city}
              onChange={(event) => handleChange("city", event.target.value)}
            />
          </Form.Group>

          <div className="mb-3">
            <Form.Label>{t("quote.holder.location")}</Form.Label>
            {!changingZip ? (
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="fw-semibold">
                  {[
                    formValues.zipCode,
                    [formValues.stateCode, formValues.stateName]
                      .filter(Boolean)
                      .join(" - "),
                    countyOptions.find((o) => o.fips === formValues.countyFips)
                      ?.name,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                <Button
                  type="button"
                  variant="link"
                  className="p-0"
                  onClick={() => setChangingZip(true)}
                  disabled={isSubmitting}
                >
                  {t("quote.holder.changeZip")}
                </Button>
              </div>
            ) : (
              <div>
                <div className="d-flex gap-2 align-items-center">
                  <Form.Control
                    autoFocus
                    inputMode="numeric"
                    autoComplete="postal-code"
                    value={zipQuery}
                    placeholder={t("quote.holder.zipSearchPlaceholder")}
                    onChange={(event) =>
                      handleZipQueryChange(event.target.value)
                    }
                  />
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => {
                      setChangingZip(false);
                      setZipQuery("");
                      setZipSuggestions([]);
                      setZipError(null);
                    }}
                  >
                    {t("buttons.cancel")}
                  </Button>
                </div>
                {loadingZip && (
                  <span className="input-hint">
                    {t("wizard.householdBasics.loadingZip")}
                  </span>
                )}
                {zipError && <span className="form-error">{zipError}</span>}
                {zipSuggestions.length > 0 && (
                  <div className="zip-suggestions">
                    {zipSuggestions.map((suggestion) => (
                      <button
                        type="button"
                        key={`${suggestion.zipCode}-${suggestion.city}`}
                        onClick={() => lookupZip(suggestion.zipCode)}
                      >
                        <strong>{suggestion.zipCode}</strong>
                        <span>
                          {suggestion.city}, {suggestion.stateName}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!changingZip && countyOptions.length > 1 && (
              <Form.Select
                className="mt-2"
                aria-label={t("quote.holder.county")}
                value={formValues.countyFips ?? ""}
                onChange={(event) =>
                  handleChange("countyFips", event.target.value)
                }
              >
                {countyOptions.map((option) => (
                  <option key={option.fips} value={option.fips}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </div>

          <Form.Group controlId="holderSsn" className="mb-3">
            <Form.Label>{t("quote.holder.ssn")}</Form.Label>
            <Form.Control
              value={formValues.ssn}
              onChange={(event) => handleChange("ssn", event.target.value)}
              placeholder="000-00-0000"
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="holderUsesTobacco"
                label={t("quote.holder.usesTobacco")}
                checked={Boolean(formValues.usesTobacco)}
                onChange={(event) =>
                  handleChange("usesTobacco", event.target.checked)
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="holderIsPregnant"
                label={t("quote.holder.isPregnant")}
                checked={Boolean(formValues.isPregnant)}
                onChange={(event) =>
                  handleChange("isPregnant", event.target.checked)
                }
              />
            </Col>
          </Row>

          <div className="form-actions">
            <Button
              type="submit"
              className="button button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("quote.holder.saving")}
                </>
              ) : (
                t("buttons.continue")
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
